import {Component, Inject, Input} from '@angular/core';
import {AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {validators} from '@emma-labs/shared/angular';
import {deepEqual, ENVIRONMENT, EnvironmentBroker, QuestionnaireId} from '@emma-labs/shared/typescript';
import {ButtonModule} from 'primeng/button';
import {CardModule} from 'primeng/card';
import {CheckboxModule} from 'primeng/checkbox';
import {InputTextareaModule} from 'primeng/inputtextarea';
import {RequestableExtraQuestionnaire} from '../../../entities/underwriting-page-object/underwriting-page-object';
import {BaseSectionComponent} from '../../base-section/base-section.component';

export type RequestableExtraQuestionnairesForm = {
  [key: string]: AbstractControl<unknown, unknown>;
  customQuestion: FormControl<string | null>;
};

@Component({
  selector: 'em-requestable-extra-questionnaires-section',
  templateUrl: './requestable-extra-questionnaires-section.component.html',
  imports: [CheckboxModule, ReactiveFormsModule, CardModule, ButtonModule, InputTextareaModule],
})
export class RequestableExtraQuestionnairesSectionComponent extends BaseSectionComponent<
  FormGroup<RequestableExtraQuestionnairesForm>,
  RequestableExtraQuestionnaire[],
  QuestionnaireId[] | string
> {
  customQuestionnaireBtnLoading = false;
  presetQuestionnaireBtnLoading = false;
  extraQuestionnaires: RequestableExtraQuestionnaire[] | undefined = [];

  @Input({required: true})
  override set props(requestableExtraQuestionnaire: RequestableExtraQuestionnaire[]) {
    if (!requestableExtraQuestionnaire || requestableExtraQuestionnaire.length < 0) {
      throw new Error('Missing extra questionnaires');
    }

    if (!this.extraQuestionnaires || this.extraQuestionnaires.length <= 0) {
      this.buildForm(requestableExtraQuestionnaire);
    }

    if (!deepEqual(this.extraQuestionnaires, requestableExtraQuestionnaire)) {
      this.updateForm(requestableExtraQuestionnaire);
    }

    this.extraQuestionnaires = requestableExtraQuestionnaire;
  }

  constructor(
    private readonly fb: FormBuilder,
    @Inject(ENVIRONMENT) environment: EnvironmentBroker,
  ) {
    super(environment);
    this.form = this.createForm();
  }

  submitCustomQuestionnaire(): void {
    this.customQuestionnaireBtnLoading = true;

    if (!this.form.controls.customQuestion.value) {
      throw new Error('question is required');
    }

    this.propsChange.emit({
      output: this.form.controls.customQuestion.value?.trim(),
      callback: (status) => {
        if (status) {
          this.form.controls.customQuestion.reset();
        }
        this.customQuestionnaireBtnLoading = false;
      },
    });
  }

  submitPresetQuestionnaire(): void {
    this.presetQuestionnaireBtnLoading = true;

    const questionnaireIds = Object.keys(this.form.value).filter(
      (key) => Object.values(QuestionnaireId).includes(key as QuestionnaireId) && this.form.get(key)?.value,
    );

    this.propsChange.emit({
      output: questionnaireIds as QuestionnaireId[],
      callback: (_) => (this.presetQuestionnaireBtnLoading = false),
    });
  }

  getFormControl(key: QuestionnaireId): FormControl<QuestionnaireId> {
    return this.form.get(key) as FormControl;
  }

  private buildForm(extraQuestionnaires: RequestableExtraQuestionnaire[]): void {
    extraQuestionnaires.forEach((eq) => {
      const control: FormControl<boolean | null> = this.fb.control(null);
      this.form.addControl(eq.id, control);
      if (eq.selected) {
        control.setValue(true);
        control.disable();
      }
    });

    this.form.setValidators(validators.atLeastOneChecked);
  }

  private updateForm(extraQuestionnaires: RequestableExtraQuestionnaire[]): void {
    extraQuestionnaires.forEach((eq) => {
      if (eq.selected) {
        this.form.get(eq.id)?.disable();
      }
    });
  }

  private createForm(): FormGroup<RequestableExtraQuestionnairesForm> {
    return this.fb.group<RequestableExtraQuestionnairesForm>({
      customQuestion: this.fb.control(null, [validators.notEmpty]),
    });
  }
}
